import React, { useState, useMemo, useEffect, useCallback } from "react";
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar'; 
import Toolbar from '@mui/material/Toolbar'; 
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import AddIcon from '@mui/icons-material/Add';
import Card from '@mui/material/Card'; 
import CardActionArea from '@mui/material/CardActionArea'; 
import Button from '@mui/material/Button'; 
import InputAdornment from '@mui/material/InputAdornment'; 
import SearchIcon from '@mui/icons-material/Search'; 
import CreateIdealCandidateForm from '../CreateIdealCandidateForm/CreateIdealCandidateForm';
import {FormControlLabel, FormGroup, Switch, alpha, useTheme, Grid, List, ListItemButton, ListItemIcon, ListItemText, Divider, InputBase, Link } from "@mui/material";
import {Link as RouterLink, useNavigate} from "react-router-dom"; 
import HistoryIcon from '@mui/icons-material/History'; 
import ArticleIcon from '@mui/icons-material/Article'; 
import  { useTour } from '../../shared/TourContext';

const COMPANY_PRIMARY_COLOR = '#0101FF'; 

// --- Constants ---
const LOCAL_STORAGE_RECENTLY_VIEWED_KEY = 'ct_recentlyViewedIdealCandidates';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// --- Main Component ---
const IdealCandidatesListing = ({ idealCandidates: initialIdealCandidates, columns, onCreate }) => {
    // --- State Variables ---
    const theme = useTheme();
    const [open, setOpen] = useState(false); 
    const [includeArchived, setIncludeArchived] = useState(false);
    const [selectedTemplateTitle, setSelectedTemplateTitle] = useState(null); 
    const [selectedRecentCandidateData, setSelectedRecentCandidateData] = useState(null); 
    const [searchTerm, setSearchTerm] = useState(""); 
    const [localIdealCandidates, setLocalIdealCandidates] = useState([]);
    const navigate = useNavigate(); 

    // --- LocalStorage and State Synchronization for Recently Viewed ---
    const getRecentlyViewedMap = useCallback(() => {
        try {
            const stored = localStorage.getItem(LOCAL_STORAGE_RECENTLY_VIEWED_KEY);
            return stored ? JSON.parse(stored) : {};
        } catch (e) {
            console.error("Error reading recently viewed from localStorage", e);
            return {};
        }
    }, []);

    useEffect(() => {
        const recentlyViewedMap = getRecentlyViewedMap();
        const mergedCandidates = initialIdealCandidates.map(candidate => ({
            ...candidate,
            lastOpenedAt: recentlyViewedMap[candidate.id] || candidate.lastOpenedAt || candidate.createdAt || null,
        }));
        setLocalIdealCandidates(mergedCandidates);
    }, [initialIdealCandidates, getRecentlyViewedMap]);

    // --- Event Handlers ---
    const handleProfileViewedAndNavigate = (candidateId) => {
        const now = new Date().toISOString();
        setLocalIdealCandidates(prevCandidates =>
            prevCandidates.map(candidate =>
                candidate.id === candidateId
                    ? { ...candidate, lastOpenedAt: now }
                    : candidate
            )
        );
        try {
            const recentlyViewedMap = getRecentlyViewedMap();
            recentlyViewedMap[candidateId] = now;
            const recentKeys = Object.keys(recentlyViewedMap).sort((a,b) => new Date(recentlyViewedMap[b]).getTime() - new Date(recentlyViewedMap[a]).getTime());
            if(recentKeys.length > 20) { 
                const oldestKey = recentKeys[recentKeys.length -1];
                delete recentlyViewedMap[oldestKey];
            }
            localStorage.setItem(LOCAL_STORAGE_RECENTLY_VIEWED_KEY, JSON.stringify(recentlyViewedMap));
        } catch (e) {
            console.error("Error updating recently viewed in localStorage", e);
        }
        navigate(`/ideal-candidates/${candidateId}`);
    };

    const handleOpenCreateDialog = (templateTitle = null, initialData = null) => {
        setSelectedTemplateTitle(templateTitle);
        setSelectedRecentCandidateData(initialData); 
        setOpen(true);
    };

    const handleCreateIdealCandidateClose = () => {
        setOpen(false);
        setSelectedTemplateTitle(null); 
        setSelectedRecentCandidateData(null);
    };

    const handleIncludeArchivedChange = (event) => {
        setIncludeArchived(event.target.checked);
    }

    const handleCreateIdealCandidate = (values) => {
        const submissionValues = { ...values, template: selectedTemplateTitle };
        if (onCreate) {
            onCreate(submissionValues)
                .then(() => { setOpen(false); setSelectedTemplateTitle(null); setSelectedRecentCandidateData(null); })
                .catch(e => console.error("Error in onCreate promise:", e));
        } else {
            console.error("onCreate prop is not defined");
            setOpen(false); setSelectedTemplateTitle(null); setSelectedRecentCandidateData(null);
        }
    };

    // --- Dialog Definition ---
    const dialog = (
        <Dialog 
            open={open} 
            onClose={handleCreateIdealCandidateClose}  
            TransitionComponent={Transition}
            PaperProps={{ sx: { backgroundColor: theme.palette.background.default } }}
        >
            <AppBar sx={{ position: 'relative', backgroundColor: COMPANY_PRIMARY_COLOR, color: theme.palette.getContrastText(COMPANY_PRIMARY_COLOR) }}>
            </AppBar>
            <DialogContent sx={{backgroundColor: theme.palette.background.default, p: {xs: 2, sm:3, md: 4} }}>
                <CreateIdealCandidateForm 
                    onSubmit={handleCreateIdealCandidate}
                    initialData={selectedRecentCandidateData} 
                />
            </DialogContent>
        </Dialog>
    );
    
    // --- Memoized Data for Display ---
    const filteredIdealCandidatesForTable = useMemo(() => {
        let filtered = localIdealCandidates;
        if (!includeArchived) {
            filtered = filtered.filter(candidate => !candidate.archivedAt);
        }
        if (searchTerm) {
            filtered = filtered.filter(candidate => 
                candidate.rawTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (typeof candidate.values?.description === 'string' && candidate.values.description.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        return filtered;
    }, [localIdealCandidates, includeArchived, searchTerm]);
    
    const mostRecentJobsForCards = useMemo(() => {
        if (!localIdealCandidates || localIdealCandidates.length === 0) { return []; }
        return [...localIdealCandidates]
            .filter(candidate => !candidate.archivedAt) 
            .sort((a, b) => {
                const timeA = a.lastOpenedAt ? new Date(a.lastOpenedAt).getTime() : 0;
                const timeB = b.lastOpenedAt ? new Date(b.lastOpenedAt).getTime() : 0;
                if (isNaN(timeA) || isNaN(timeB)) { return isNaN(timeA) ? 1 : -1; }
                return timeB - timeA;
            })
            .slice(0, 4); 
    }, [localIdealCandidates]);

    // --- Conditional Rendering for Table/Empty State ---
    let listingContent;
    if (!filteredIdealCandidatesForTable.length) {
        listingContent = ( 
            <Box sx={{p: {xs:3, sm:4, md: 6}, textAlign: 'center', mt: 4}}>
                <ArticleIcon sx={{ fontSize: 72, color: theme.palette.grey[400], mb: 2.5 }} /> 
                <Typography variant="h6" color="text.secondary" sx={{mb:1.5, fontWeight: 400}}>
                    {searchTerm ? "No profiles match your search" : "No Ideal Candidate Profiles"}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{maxWidth: '480px', mx: 'auto', lineHeight: 1.6}}>
                    {searchTerm ? "Try adjusting your search terms or filters." : 'Click the "New Profile" button to create your first ideal candidate profile.'}
                </Typography>
                {!searchTerm && (
                     <Button 
                        variant="contained" 
                        sx={{
                            mt: 3.5, borderRadius: '24px', px:3.5, py: 1.25, textTransform: 'none', fontSize: '1rem', 
                            backgroundColor: COMPANY_PRIMARY_COLOR, 
                            color: theme.palette.getContrastText(COMPANY_PRIMARY_COLOR),
                            boxShadow: theme.shadows[2], 
                            '&:hover': {
                                backgroundColor: alpha(COMPANY_PRIMARY_COLOR, 0.85),
                                boxShadow: theme.shadows[4]
                            }
                        }}
                        startIcon={<AddIcon />} 
                        onClick={() => handleOpenCreateDialog("Blank Profile")}
                    >
                        New Profile
                    </Button>
                )}
            </Box>
        );
    } else {
        listingContent = (
            <Box mt={0}> 
                <TableContainer 
                    sx={{
                        borderRadius: '8px', 
                        border: `1px solid ${theme.palette.divider}`, 
                        boxShadow: 'none',
                        backgroundColor: theme.palette.background.paper, 
                    }}
                >
                    <Table sx={{ minWidth: 650 }} aria-label="ideal candidates table">
                        <TableHead>
                            <TableRow sx={{ '& th': { borderBottom: `1px solid ${theme.palette.divider}` }}}>
                                {columns.map((column) => (
                                    <TableCell 
                                        align={column.align || "left"} 
                                        key={column.id} 
                                        sx={{
                                            fontWeight: 500, 
                                            color: theme.palette.text.secondary,
                                            py: 1.25, px:2.5, 
                                            fontSize: '0.875rem',
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: alpha(COMPANY_PRIMARY_COLOR, 0.04), 
                                            textTransform: 'none'
                                        }}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredIdealCandidatesForTable.map((idealCandidate) => (
                                <TableRow
                                    key={idealCandidate.id}
                                    hover
                                    sx={{ 
                                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                                        '&:hover': { backgroundColor: alpha(COMPANY_PRIMARY_COLOR, 0.08) }, 
                                        '& td, & th': { borderBottom: `1px solid ${theme.palette.divider}` } 
                                    }}
                                >
                                    {columns.map((column, colIndex) => (
                                        <TableCell 
                                            align={column.align || "left"} 
                                            key={column.id} 
                                            sx={{
                                                py: 1.5, px:2.5, 
                                                color: theme.palette.text.primary, 
                                                fontSize: '0.875rem'
                                            }}
                                        >
                                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                {colIndex === 0 && <ArticleIcon sx={{fontSize: '1.25rem', color: COMPANY_PRIMARY_COLOR, mr: 1.5}} />} 
                                                {column.id === 'title' ? (
                                                    <Link 
                                                        component="button" 
                                                        onClick={() => handleProfileViewedAndNavigate(idealCandidate.id)}
                                                        sx={{ 
                                                            color: COMPANY_PRIMARY_COLOR, 
                                                            fontWeight: 500,
                                                            textAlign: 'left', textDecoration: 'none',
                                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                            maxWidth: "450px", display: 'block', p:0, fontSize: 'inherit', 
                                                            '&:hover': { textDecoration: 'underline'}
                                                        }}
                                                    >
                                                        {idealCandidate.rawTitle || idealCandidate.values.title || "Untitled Profile"}
                                                    </Link>
                                                ) : (
                                                    <Typography variant="body2" component="span" sx={{
                                                        display: "block", maxWidth: "500px", 
                                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                        fontWeight: colIndex === 0 ? 500 : 400, 
                                                        color: theme.palette.text.secondary, 
                                                    }}>
                                                        {idealCandidate.values[column.id]}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    }
    
    // --- Page specific tour stuff ---
    const { setSteps } = useTour();
    const [run, setRun] = useState(true);
    const [stepIndex, setStepIndex] = useState(0);

    useEffect(() => {
        setSteps([
            {
                target: 'body',
                title: 'Welcome to Candidate Tools',
                content: (
                    <div className="space-y-2">
                        <p>Let's take a quick tour to help you get started with managing candidate profiles.</p>
                        <p className="text-sm text-gray-500">You can exit anytime by clicking the "X" button.</p>
                    </div>
                ),
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '[data-tour="new-profile-button"]',
                title: '➕ Create New Profiles',
                content: (
                    <div className="space-y-2">
                        <p>Click here to add new candidates profiles.</p>
                        <p className="text-sm text-gray-500">You'll be able to enter all relevant candidate information in the next steps.</p>
                    </div>
                ),
                disableBeacon: true,
                placement: "right",
            },
            {
                target: '[data-tour="search-bar"]',
                title: '🔍 Find Candidates',
                content: (
                    <div className="space-y-2">
                        <p>Quickly search by job title.</p>
                    </div>
                ),
                disableBeacon: true,
                placement: "bottom",
            }
          ]);
        
        return () => setSteps([]); 
      }, []);

    // --- Main Render ---
    return (
        <Box sx={{ 
            backgroundColor: theme.palette.background.default, 
            minHeight: 'calc(100vh - 64px)', 
            p: { xs: 2, sm: 3, md: 3.5 }, 
            display: 'flex',
            flexDirection: 'column'
        }}>
          {dialog}
          
          {/* Section: Page Title */}
          <Typography variant="h4" sx={{ 
              fontWeight: 400, 
              color: theme.palette.text.primary, 
              fontSize: '1.75rem',
              marginY: '16px', 
              mb: 2, 
            }}>
             Ideal Candidate Profiles
            </Typography>

            {/* Section: Search Bar */}
            <Paper data-tour="search-bar"
                elevation={0}
                component="form" 
                onSubmit={(e) => e.preventDefault()} 
                sx={{ 
                    p: '4px 8px', display: 'flex', alignItems: 'center', 
                    width: '100%', maxWidth: '720px', mb: 3, 
                    borderRadius: '28px', 
                    backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[800],
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: `0 1px 2px 0 ${alpha(theme.palette.common.black, 0.05)}`,
                    '&:hover': {
                        boxShadow: `0 1px 3px 0 ${alpha(theme.palette.common.black, 0.1)}`,
                        backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[700],
                    }
                }}
            >
                <IconButton sx={{ p: '10px' }} aria-label="search">
                    <SearchIcon sx={{color: theme.palette.text.secondary}}/>
                </IconButton>
                <InputBase
                    sx={{ ml: 1, flex: 1, fontSize: '1rem' }}
                    placeholder="Search profiles..."
                    inputProps={{ 'aria-label': 'search ideal candidate profiles' }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </Paper>

            {/* Section: Actions (New Profile Button & Show Archived Switch) */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Button data-tour="new-profile-button"
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={() => handleOpenCreateDialog("Blank Profile")}
                    sx={{ 
                        borderRadius: '20px', textTransform: 'none', 
                        backgroundColor: COMPANY_PRIMARY_COLOR,
                        color: theme.palette.getContrastText(COMPANY_PRIMARY_COLOR),
                        boxShadow: theme.shadows[2], py: '10px', px: '24px',
                        fontWeight: 500, fontSize: '0.9375rem',
                        '&:hover': { 
                            backgroundColor: alpha(COMPANY_PRIMARY_COLOR, 0.85), 
                            boxShadow: theme.shadows[4],
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    New Profile
                </Button>
                <FormGroup sx={{ml: 'auto'}}> 
                    <FormControlLabel 
                        control={
                        <Switch 
                            checked={includeArchived}
                            onChange={handleIncludeArchivedChange} 
                            sx={{ 
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: COMPANY_PRIMARY_COLOR,
                                  '&:hover': { backgroundColor: alpha(COMPANY_PRIMARY_COLOR, theme.palette.action.hoverOpacity)},
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: COMPANY_PRIMARY_COLOR },
                              }}
                            size="small" 
                        />
                        } 
                        label={ <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}> Show Archived </Typography> } 
                    />
                </FormGroup>
            </Box>
      
          {/* Section: Quick Access Cards */}
          {mostRecentJobsForCards.length > 0 && (
            <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ 
                  color: theme.palette.text.primary, display: 'block',
                  mb: 2, fontSize: '1rem', fontWeight: 500,
                }}>
                  Quick Access
                </Typography>
                <Grid container spacing={2.5}> 
                  {mostRecentJobsForCards.map((candidate) => (
                    <Grid item xs={12} sm={6} md={3} key={candidate.id}> 
                      <Paper 
                        variant="outlined"
                        sx={{
                          borderRadius: '12px', borderColor: theme.palette.divider,
                          backgroundColor: theme.palette.background.paper, 
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': { boxShadow: theme.shadows[3], borderColor: COMPANY_PRIMARY_COLOR, transform: 'translateY(-2px)'}
                        }}
                      >
                        <ListItemButton 
                          onClick={() => handleProfileViewedAndNavigate(candidate.id)} 
                          sx={{ p:2, textAlign: 'left', display: 'flex', alignItems: 'center', borderRadius: '12px' }}
                        >
                          <ListItemIcon sx={{minWidth: 'auto', mr: 1.5, color: COMPANY_PRIMARY_COLOR }}> 
                              <HistoryIcon /> 
                          </ListItemIcon>
                          <ListItemText 
                            primary={ <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {typeof candidate.rawTitle === 'string' && candidate.rawTitle.trim() !== '' ? candidate.rawTitle : 'Untitled Profile'}
                                </Typography>
                            }
                            secondary={ <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem'}}>
                                    {candidate.lastOpenedAt ? `Opened: ${new Date(candidate.lastOpenedAt).toLocaleDateString()}` : (candidate.createdAt ? `Created: ${new Date(candidate.createdAt).toLocaleDateString()}` : '')}
                                </Typography>
                            }
                          />
                        </ListItemButton>
                      </Paper>
                    </Grid> 
                  ))}
                </Grid>
              </Box>
          )}

          {/* Section: Main Table Listing */}
          {listingContent}
        </Box>
      );
};

export default IdealCandidatesListing;